import { apiClient } from './client';

export type DocumentSummary = {
  id: string;
  title: string;
  description?: string;
  originalFilename?: string;
  mimeType?: string;
  size?: number;
  url: string;
  createdAt?: string;
};

export type DocumentSyncSummary = {
  synced: number;
  skipped: number;
  errors: number;
  loaded: number;
};

export const listDocuments = async () => {
  const { data } = await apiClient.get<DocumentSummary[]>('/documents');
  return data;
};

export const uploadDocument = async (file: File, description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) {
    formData.append('description', description);
  }

  const { data } = await apiClient.post<DocumentSummary>('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

export const replaceDocumentContent = async (
  id: string,
  file: File | Blob,
  options: { filename?: string; skipSync?: boolean } = {},
) => {
  const formData = new FormData();
  const filename = options.filename ?? (file instanceof File ? file.name : 'document.md');
  formData.append('file', file, filename);

  const url = options.skipSync ? `/documents/${id}/content?skipSync=1` : `/documents/${id}/content`;
  const { data } = await apiClient.put<DocumentSummary>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

export const deleteDocument = async (id: string) => {
  await apiClient.delete(`/documents/${id}`);
};

export const syncDocuments = async () => {
  const { data } = await apiClient.post<DocumentSyncSummary>('/documents/sync');
  return data;
};

export interface DocSyncReplicaStatus {
  replicaId: string;
  moduleInstanceId: string;
  lastSeenVersion: string | null;
  docCount: number;
  loadedAt: string | null;
  manifestVersion: string | null;
  supabaseVersion: string | null;
  supabaseUpdatedAt: string | null;
  supabaseTriggeredBy: string | null;
  inSync: boolean;
  checkedAt: string;
}

export const getDocSyncStatus = async () => {
  const { data } = await apiClient.get<DocSyncReplicaStatus>('/documents/sync-status');
  return data;
};

export const probeDocSyncReplicas = async (sampleCount = 8): Promise<DocSyncReplicaStatus[]> => {
  const samples: DocSyncReplicaStatus[] = [];
  for (let i = 0; i < sampleCount; i += 1) {
    try {
      const sample = await getDocSyncStatus();
      samples.push(sample);
    } catch {}
  }
  const byReplica = new Map<string, DocSyncReplicaStatus>();
  for (const sample of samples) {
    byReplica.set(sample.replicaId, sample);
  }
  return Array.from(byReplica.values()).sort((a, b) => a.replicaId.localeCompare(b.replicaId));
};
